import { useToastContext } from "@ttc3k/trekker";
import { useAppContext } from "AppContext";
import {
  EnumEntityReferenceType,
  EnumOwnerType,
  NoteManyDocument,
  NoteManyQuery,
  SortFindManyNoteInput,
  useNoteCreateOneMutation,
  useNoteManyQuery,
  useNoteRemoveByIdMutation,
  useNoteUpdateByIdMutation,
} from "gql/generated";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoggedInAs } from "shared/hooks";

export type UseNotesProps = {
  _entityId: string;
  type: EnumEntityReferenceType;
  getNoteCreatingState?: ({
    isCreatingNote,
    setIsCreatingNote,
  }: {
    isCreatingNote: boolean;
    setIsCreatingNote: Dispatch<SetStateAction<boolean>>;
  }) => void;
};

export const useNotes = ({
  _entityId,
  type,
  getNoteCreatingState,
}: UseNotesProps) => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();

  const { dbUser } = useAppContext();
  const loggedInAs = useLoggedInAs();
  const organizationId = loggedInAs?.entityId;
  if (!organizationId) {
    throw new Error("You must be logged in as an organization to use Notes");
  }

  // Load the notes for this list
  const notesRef = useRef<NoteManyQuery["noteMany"][0][]>([]);

  // Keep track of the resolved and unresolved notes
  const [unResolvedNotes, setUnResolvedNotes] = useState<
    NoteManyQuery["noteMany"][0][]
  >([]);
  const [resolvedNotes, setResolvedNotes] = useState<
    NoteManyQuery["noteMany"][0][]
  >([]);

  const updateNoteGroups = () => {
    setUnResolvedNotes(
      notesRef.current.filter((note) => !note.resolvedBy) ?? [],
    );
    setResolvedNotes(
      notesRef.current.filter((note) => !!note.resolvedBy) ?? [],
    );
  };

  const { loading, error } = useNoteManyQuery({
    variables: {
      filter: {
        entity: {
          _entityId,
          type,
        },
        owner: { entityId: organizationId, type: EnumOwnerType.Organization },
      },
      sort: SortFindManyNoteInput.UpdatedDesc,
      limit: 10000,
    },
    onCompleted: (data) => {
      notesRef.current = data?.noteMany;
      updateNoteGroups();
    },
  });

  const isSomeUnresolvedNotes = unResolvedNotes && unResolvedNotes.length > 0;

  // Keep track of new note creation AND pass the method back to the parent
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  useEffect(() => {
    getNoteCreatingState?.({
      isCreatingNote,
      setIsCreatingNote,
    });
  }, [isCreatingNote, getNoteCreatingState]);

  // Handle loading and resolved view states
  const [loadingNoteId, setLoadingNoteId] = useState("");
  const [showResolvedNotes, setShowResolvedNotes] = useState(false);

  /**
   * Handle note creation
   */
  const [createNote] = useNoteCreateOneMutation({
    refetchQueries: [NoteManyDocument],
  });

  const handleCreateNote = async (message: string) => {
    setLoadingNoteId("newNote");
    try {
      if (dbUser && dbUser._id) {
        const note = await createNote({
          variables: {
            record: {
              author: dbUser._id,
              entity: {
                _entityId,
                type,
              },
              message,
              owner: {
                entityId: organizationId,
                type: EnumOwnerType.Organization,
              },
            },
          },
        });

        if (note.data?.noteCreateOne?.record) {
          notesRef.current = [
            note.data.noteCreateOne.record,
            ...notesRef.current,
          ];
          updateNoteGroups();
        }
      }
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:NOTES.ERROR.CREATING"),
      });
    } finally {
      setLoadingNoteId("");
      setIsCreatingNote(false);
    }
  };

  /**
   * Handle note updating (either note text or resolving)
   */
  const [updateNote] = useNoteUpdateByIdMutation();

  const handleUpdateNoteMessage = async (noteId: string, message: string) => {
    setLoadingNoteId(noteId);
    try {
      const note = await updateNote({
        variables: {
          id: noteId,
          record: {
            message,
          },
        },
      });

      if (note.data?.noteUpdateById?.record) {
        notesRef.current = notesRef.current.map((item) =>
          item._id === note.data?.noteUpdateById?.record?._id
            ? (note.data?.noteUpdateById
                ?.record as NoteManyQuery["noteMany"][0])
            : item,
        );
        updateNoteGroups();
      }
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:NOTES.ERROR.UPDATING"),
      });
    } finally {
      setLoadingNoteId("");
    }
  };

  const handleResolveNote = async (noteId: string) => {
    try {
      if (dbUser && dbUser._id) {
        const note = await updateNote({
          variables: {
            id: noteId,
            record: {
              resolvedBy: dbUser._id,
            },
          },
        });

        if (note.data?.noteUpdateById?.record) {
          notesRef.current = notesRef.current.map((item) =>
            item._id === note.data?.noteUpdateById?.record?._id
              ? (note.data?.noteUpdateById
                  ?.record as NoteManyQuery["noteMany"][0])
              : item,
          );
          updateNoteGroups();
        }
      }
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:NOTES.ERROR.UPDATING"),
      });
    }
  };

  /**
   * Handle note removal
   */
  const [removeNote] = useNoteRemoveByIdMutation({
    refetchQueries: [NoteManyDocument],
  });

  const handleRemoveNote = async (noteId: string) => {
    setLoadingNoteId(noteId);
    try {
      const note = await removeNote({
        variables: { id: noteId },
      });

      if (note.data?.noteRemoveById?.record) {
        notesRef.current = notesRef.current.filter(
          (item) => item._id !== note.data?.noteRemoveById?.record?._id,
        );
        updateNoteGroups();
      }
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("core:NOTES.ERROR.REMOVING"),
      });
    } finally {
      setLoadingNoteId("");
    }
  };

  return {
    error,
    isCreatingNote,
    isSomeUnresolvedNotes,
    loading,
    loadingNoteId,
    resolvedNotes,
    showResolvedNotes,
    unResolvedNotes,
    onRemoveNote: handleRemoveNote,
    onResolveNote: handleResolveNote,
    onUpdateNoteMessage: handleUpdateNoteMessage,
    onCreateNote: handleCreateNote,
    setIsCreatingNote,
    setShowResolvedNotes,
  };
};
